/*
 * Copyright (c) 2025 Ping Identity Corporation. All rights reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
/**
 * Base class for authentication tree callback wrappers.
 */
export class BaseCallback {
    payload;
    /**
     * @param payload The raw payload returned by OpenAM
     */
    constructor(payload) {
        this.payload = payload;
    }
    /**
     * Gets the name of this callback type.
     */
    getType() {
        return this.payload.type;
    }
    /**
     * Gets the value of the specified input element, or the first element if `selector` is not
     * provided.
     *
     * @param selector The index position or name of the desired element
     */
    getInputValue(selector = 0) {
        return this.getArrayElement(this.payload.input, selector).value;
    }
    /**
     * Sets the value of the specified input element, or the first element if `selector` is not
     * provided.
     *
     * @param selector The index position or name of the desired element
     */
    setInputValue(value, selector = 0) {
        this.getArrayElement(this.payload.input, selector).value = value;
    }
    /**
     * Gets the value of the specified output element, or the first element if `selector`
     * is not provided.
     *
     * @param selector The index position or name of the desired element
     */
    getOutputValue(selector = 0) {
        return this.getArrayElement(this.payload.output, selector).value;
    }
    /**
     * Gets the value of the first output element with the specified name or the
     * specified default value.
     *
     * @param name The name of the desired element
     */
    getOutputByName(name, defaultValue) {
        const output = this.payload.output.find((x) => x.name === name);
        return output ? output.value : defaultValue;
    }
    getArrayElement(array, selector = 0) {
        if (array === undefined) {
            throw new Error(`No NameValue array was provided to search (selector ${selector})`);
        }
        if (typeof selector === 'number') {
            if (selector < 0 || selector > array.length - 1) {
                throw new Error(`Selector index ${selector} is out of range`);
            }
            return array[selector];
        }
        if (typeof selector === 'string') {
            const input = array.find((x) => x.name === selector);
            if (!input) {
                throw new Error(`Missing callback input entry "${selector}"`);
            }
            return input;
        }
        // Duck typing for RegEx
        if (typeof selector === 'object' && selector.test && Boolean(selector.exec)) {
            const input = array.find((x) => selector.test(x.name));
            if (!input) {
                throw new Error(`Missing callback input entry "${selector}"`);
            }
            return input;
        }
        throw new Error('Invalid selector value type');
    }
}
